// GenerationRegistry — registry de "sessões de geração" por conversa.
//
// Cada conversa que está gerando (ou que tem uma sessão de exibição ativa)
// tem aqui uma instância `Chat` do AI SDK, que VIVE INDEPENDENTE de qualquer
// componente React. Como `useChat({ chat })` é só uma view (useSyncExternalStore)
// que não aborta o chat no unmount, trocar de conversa/aba não interrompe o
// stream — ele continua no registry, em segundo plano.
//
// Responsabilidades:
//  - criar/guardar/evictar Chats por conversationId (lifecycle "só enquanto gera")
//  - iniciar geração de TEXTO (Chat + transport com payload fixo) e de IMAGEM (SSE)
//  - estado reativo para a sidebar: quem está gerando, quem tem "resposta nova"
//  - ao terminar uma geração de conversa NÃO-ativa: marca não-lido + notifica
//    (Notifications API, sem push) + badge no título/favicon
//
// Ver docs/plano-streaming-concorrente.md e docs/adr/0001.

import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { Chat } from "@ai-sdk/react";
import { DefaultChatTransport, UIMessage } from "ai";
import { useQueryClient } from "@tanstack/react-query";
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/integrations/supabase/client";
import { useNavigation } from "@/contexts/NavigationContext";
import { toast } from "sonner";
import { notify } from "@/lib/notifications";
import { setUnreadBadge } from "@/lib/tabBadge";

export type UnreadKind = "new" | "failed";

/** Payload extra (além de `messages`) enviado ao edge function `chat`. */
export type TextSendBody = Record<string, unknown>;

export interface StartTextConfig {
  body: TextSendBody;
  userMessage: { role: "user"; parts: Array<{ type: "text"; text: string }> };
  /** Histórico para semear o Chat ao criá-lo (ignorado se o Chat já existe). */
  seedMessages?: UIMessage[];
}

export interface StartImageConfig {
  prompt: string;
  model: string;
  systemPrompt?: string;
  /** Texto da mensagem do usuário a exibir. */
  userText: string;
}

interface ConvSession {
  chat: Chat<UIMessage>;
  configRef: { current: TextSendBody | null };
  kind: "text" | "image";
  imageAbort?: AbortController;
}

interface GenerationContextValue {
  ensureTextChat: (convId: string, seedMessages?: UIMessage[]) => Chat<UIMessage>;
  getChat: (convId: string) => Chat<UIMessage> | undefined;
  startText: (convId: string, cfg: StartTextConfig) => void;
  startImage: (convId: string, cfg: StartImageConfig) => Promise<void>;
  stop: (convId: string) => Promise<void>;
  releaseView: (convId: string) => void;
  generating: ReadonlySet<string>;
  unread: ReadonlyMap<string, UnreadKind>;
  imageStatus: ReadonlyMap<string, string>;
  isGenerating: (convId: string) => boolean;
  imageStatusFor: (convId: string) => string | undefined;
  unreadFor: (convId: string) => UnreadKind | undefined;
  clearUnread: (convId: string) => void;
}

const GenerationContext = createContext<GenerationContextValue | undefined>(undefined);

function extractGeneratedImageUrl(content: string | null | undefined): string | null {
  if (!content) return null;
  const match = content.match(/!\[[^\]]*\]\((https?:\/\/[^)]+)\)/);
  return match?.[1] ?? null;
}

/** A aba do navegador NÃO está visível (outra aba/app na frente, ou minimizada). */
function isTabHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState !== "visible";
}

export const GenerationProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { activeConversationId, setActiveConversationId, navigateToTab } = useNavigation();

  // Ponteiro da conversa ativa lido por callbacks sem stale closure.
  const activeConvRef = useRef<string | null>(activeConversationId);
  activeConvRef.current = activeConversationId;

  // Mapa de sessões — identidade estável, não dispara render.
  const sessionsRef = useRef<Map<string, ConvSession>>(new Map());

  // Estado reativo (sidebar + ChatPage consomem).
  const [generating, setGenerating] = useState<ReadonlySet<string>>(new Set());
  const [unread, setUnread] = useState<ReadonlyMap<string, UnreadKind>>(new Map());
  const [imageStatus, setImageStatus] = useState<ReadonlyMap<string, string>>(new Map());

  const addGenerating = useCallback((convId: string) => {
    setGenerating((prev) => {
      if (prev.has(convId)) return prev;
      const next = new Set(prev);
      next.add(convId);
      return next;
    });
  }, []);
  const removeGenerating = useCallback((convId: string) => {
    setGenerating((prev) => {
      if (!prev.has(convId)) return prev;
      const next = new Set(prev);
      next.delete(convId);
      return next;
    });
  }, []);
  const setUnreadFor = useCallback((convId: string, kind: UnreadKind) => {
    setUnread((prev) => {
      const next = new Map(prev);
      next.set(convId, kind);
      return next;
    });
  }, []);
  const clearUnread = useCallback((convId: string) => {
    setUnread((prev) => {
      if (!prev.has(convId)) return prev;
      const next = new Map(prev);
      next.delete(convId);
      return next;
    });
  }, []);
  const setImageStatusFor = useCallback((convId: string, status: string | null) => {
    setImageStatus((prev) => {
      const next = new Map(prev);
      if (status === null) next.delete(convId);
      else next.set(convId, status);
      return next;
    });
  }, []);

  // Badge do título segue o total de não-lidos.
  useEffect(() => {
    setUnreadBadge(unread.size);
  }, [unread]);

  // Ao voltar o foco para a aba, a conversa ativa passa a estar "vista" de novo:
  // limpa seu marcador (e o título reverte se era a única não-lida). Cobre o
  // caso de terminar uma Geração com a aba em background na própria conversa ativa.
  useEffect(() => {
    const onReveal = () => {
      if (document.visibilityState === "visible" && activeConvRef.current) {
        clearUnread(activeConvRef.current);
      }
    };
    document.addEventListener("visibilitychange", onReveal);
    window.addEventListener("focus", onReveal);
    return () => {
      document.removeEventListener("visibilitychange", onReveal);
      window.removeEventListener("focus", onReveal);
    };
  }, [clearUnread]);

  const focusConversation = useCallback(
    (convId: string) => {
      setActiveConversationId(convId);
      navigateToTab("chat");
    },
    [setActiveConversationId, navigateToTab],
  );

  const notifyDone = useCallback(
    async (convId: string, failed: boolean) => {
      // Busca o título atual para a notificação (cheap, só na conclusão).
      let title = "uma conversa";
      try {
        const { data } = await supabase
          .from("conversations")
          .select("title")
          .eq("id", convId)
          .single();
        if (data?.title) title = data.title;
      } catch {
        /* ignore */
      }
      notify({
        title: failed ? "Geração interrompida" : "Resposta pronta",
        body: failed ? `A geração em "${title}" terminou com erro.` : `A resposta em "${title}" está pronta.`,
        tag: convId,
        onClick: () => focusConversation(convId),
      });
    },
    [focusConversation],
  );

  // Polling de título no primeiro turno (server gera o título async).
  const maybePollTitle = useCallback(
    async (convId: string) => {
      try {
        const { data: conv } = await supabase.from("conversations").select("title").eq("id", convId).single();
        const originalTitle = conv?.title;
        if (originalTitle && originalTitle !== "Nova conversa") return;
        const delays = [3000, 10000, 45000];
        for (const delay of delays) {
          await new Promise((r) => setTimeout(r, delay));
          queryClient.invalidateQueries({ queryKey: ["conversations"] });
          const { data: updated } = await supabase.from("conversations").select("title").eq("id", convId).single();
          if (updated?.title && updated.title !== originalTitle) break;
        }
      } catch {
        /* ignore */
      }
    },
    [queryClient],
  );

  const evictIfIdle = useCallback((convId: string) => {
    if (convId === activeConvRef.current) return; // a ativa fica ligada à view
    const sessions = sessionsRef.current;
    const s = sessions.get(convId);
    if (!s) return;
    if (s.chat.status === "streaming" || s.chat.status === "submitted") return;
    sessions.delete(convId);
  }, []);

  // Verifica se ainda há geração de imagem em voo lendo do flag local.
  const imageGeneratingRef = useRef<Set<string>>(new Set());

  const handleTextFinish = useCallback(
    (
      convId: string,
      e: { isAbort: boolean; isDisconnect: boolean; isError: boolean; finishReason?: string },
    ) => {
      removeGenerating(convId);
      queryClient.invalidateQueries({ queryKey: ["messages", convId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });

      const failed = e.isError || e.isDisconnect || e.finishReason === "error";
      const isActive = convId === activeConvRef.current;
      // "Visto" = você está olhando pra ela AGORA (é a ativa E a aba está
      // visível). Se a aba estiver oculta, mesmo a conversa ativa conta como
      // não-vista — então o título/badge atualiza e te chama de volta.
      const seen = isActive && !isTabHidden();

      if (!e.isAbort) {
        if (!seen) setUnreadFor(convId, failed ? "failed" : "new");
        // notify() auto-gate: só dispara se a aba estiver oculta.
        notifyDone(convId, failed);
      }

      maybePollTitle(convId);

      if (!isActive) setTimeout(() => evictIfIdle(convId), 1500);
    },
    [removeGenerating, queryClient, setUnreadFor, notifyDone, maybePollTitle, evictIfIdle],
  );

  const handleTextError = useCallback(
    (convId: string) => {
      removeGenerating(convId);
      queryClient.invalidateQueries({ queryKey: ["messages", convId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      const isActive = convId === activeConvRef.current;
      const seen = isActive && !isTabHidden();
      if (!seen) {
        setUnreadFor(convId, "failed");
        notifyDone(convId, true);
      }
      if (!isActive) setTimeout(() => evictIfIdle(convId), 1500);
      // Para a conversa ativa e visível, o ChatPage exibe o toast/banner detalhado.
    },
    [removeGenerating, queryClient, setUnreadFor, notifyDone, evictIfIdle],
  );

  const authHeaders = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token ?? "";
    return {
      Authorization: `Bearer ${token}`,
      apikey: SUPABASE_PUBLISHABLE_KEY,
    };
  }, []);

  const ensureTextChat = useCallback(
    (convId: string, seedMessages?: UIMessage[]): Chat<UIMessage> => {
      const sessions = sessionsRef.current;
      const existing = sessions.get(convId);
      if (existing) return existing.chat;

      const configRef: { current: TextSendBody | null } = { current: null };
      const chat = new Chat<UIMessage>({
        id: convId,
        messages: seedMessages ?? [],
        transport: new DefaultChatTransport({
          api: `${SUPABASE_URL}/functions/v1/chat`,
          headers: authHeaders,
          // Settings da Geração ficam FIXOS: o body já foi montado por
          // buildSendBody no momento do envio (ver ADR 0001). O multi-step de
          // tools roda dentro do streamText no edge function (1 request por
          // sendMessage), então o body é lido uma vez por envio.
          body: () => configRef.current ?? {},
        }),
        onFinish: (e: {
          isAbort?: boolean;
          isDisconnect?: boolean;
          isError?: boolean;
          finishReason?: string;
        }) =>
          handleTextFinish(convId, {
            isAbort: !!e?.isAbort,
            isDisconnect: !!e?.isDisconnect,
            isError: !!e?.isError,
            finishReason: e?.finishReason,
          }),
        onError: () => handleTextError(convId),
      });
      sessions.set(convId, { chat, configRef, kind: "text" });
      return chat;
    },
    [authHeaders, handleTextFinish, handleTextError],
  );

  const getChat = useCallback((convId: string): Chat<UIMessage> | undefined => {
    return sessionsRef.current.get(convId)?.chat;
  }, []);

  const startText = useCallback(
    (convId: string, cfg: StartTextConfig) => {
      const chat = ensureTextChat(convId, cfg.seedMessages);
      const session = sessionsRef.current.get(convId)!;
      session.kind = "text";
      session.configRef.current = cfg.body;
      clearUnread(convId);
      addGenerating(convId);
      // sendMessage adiciona a mensagem do usuário e dispara o stream.
      void chat.sendMessage(cfg.userMessage);
    },
    [ensureTextChat, clearUnread, addGenerating],
  );

  const recoverImageFromDb = useCallback(async (convId: string): Promise<string | null> => {
    const readLatest = async () => {
      const [{ data: conv }, { data: latest }] = await Promise.all([
        supabase.from("conversations").select("generation_status").eq("id", convId).single(),
        supabase
          .from("messages")
          .select("content")
          .eq("conversation_id", convId)
          .eq("role", "assistant")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      return {
        status: (conv as { generation_status?: string } | null)?.generation_status,
        content: latest?.content ?? null,
      };
    };
    let snap = await readLatest();
    let url = extractGeneratedImageUrl(snap.content);
    if (url) return url;
    const delays = [2000, 4000, 8000, 12000];
    for (const delay of delays) {
      if (!snap.status || snap.status !== "generating") break;
      await new Promise((r) => setTimeout(r, delay));
      snap = await readLatest();
      url = extractGeneratedImageUrl(snap.content);
      if (url) return url;
    }
    return null;
  }, []);

  const startImage = useCallback(
    async (convId: string, cfg: StartImageConfig) => {
      const chat = ensureTextChat(convId, []);
      const session = sessionsRef.current.get(convId)!;
      session.kind = "image";
      clearUnread(convId);
      addGenerating(convId);
      imageGeneratingRef.current.add(convId);
      setImageStatusFor(convId, "Gerando imagem...");

      // Adiciona a mensagem do usuário à view.
      const userMsg: UIMessage = {
        id: crypto.randomUUID(),
        role: "user",
        parts: [{ type: "text", text: cfg.userText }],
      } as UIMessage;
      try {
        chat.messages = [...chat.messages, userMsg];
      } catch {
        /* setter pode variar; reseed pós-conclusão cobre */
      }

      const abort = new AbortController();
      session.imageAbort = abort;

      const finishActiveAware = (failed: boolean) => {
        removeGenerating(convId);
        imageGeneratingRef.current.delete(convId);
        setImageStatusFor(convId, null);
        queryClient.invalidateQueries({ queryKey: ["messages", convId] });
        queryClient.invalidateQueries({ queryKey: ["conversations"] });
        const isActive = convId === activeConvRef.current;
        const seen = isActive && !isTabHidden();
        if (!seen) {
          setUnreadFor(convId, failed ? "failed" : "new");
          notifyDone(convId, failed);
        }
        if (!isActive) setTimeout(() => evictIfIdle(convId), 1500);
      };

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token ?? "";
        const edgeFnUrl = `${SUPABASE_URL}/functions/v1/generate-image`;
        const response = await fetch(edgeFnUrl, {
          method: "POST",
          signal: abort.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: SUPABASE_PUBLISHABLE_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt: cfg.prompt,
            model: cfg.model,
            conversationId: convId,
            ...(cfg.systemPrompt ? { systemPrompt: cfg.systemPrompt } : {}),
          }),
        });

        if (!response.ok || !response.body) {
          const recovered = await recoverImageFromDb(convId);
          if (recovered) {
            try {
              chat.messages = [
                ...chat.messages,
                { id: crypto.randomUUID(), role: "assistant", parts: [{ type: "text", text: `![Imagem gerada](${recovered})` }] } as UIMessage,
              ];
            } catch { /* reseed cobre */ }
            finishActiveAware(false);
            void maybePollTitle(convId);
            return;
          }
          if (convId === activeConvRef.current) toast.error("Erro na geração de imagem");
          finishActiveAware(true);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let imageUrl: string | null = null;
        let textContent: string | null = null;
        let streamError: string | null = null;

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const evt = JSON.parse(line.slice(6));
              if (evt.type === "status") setImageStatusFor(convId, evt.message);
              else if (evt.type === "result") {
                imageUrl = evt.imageUrl;
                textContent = evt.textContent;
              } else if (evt.type === "error") streamError = evt.error;
            } catch {
              /* ignore */
            }
          }
        }

        if (!imageUrl) imageUrl = await recoverImageFromDb(convId);

        if (streamError && !imageUrl) {
          if (convId === activeConvRef.current) toast.error(`Erro na geração de imagem: ${streamError}`);
          finishActiveAware(true);
          return;
        }
        if (!imageUrl) {
          if (convId === activeConvRef.current)
            toast.error("A imagem pode ter sido gerada, mas não conseguimos recuperá-la desta vez.");
          finishActiveAware(true);
          return;
        }

        const assistantContent = textContent
          ? `![Imagem gerada](${imageUrl})\n\n${textContent}`
          : `![Imagem gerada](${imageUrl})`;
        try {
          const already = chat.messages.some(
            (m) =>
              m.role === "assistant" &&
              m.parts?.some(
                (p: { type?: string; text?: string }) => p.type === "text" && p.text?.includes(imageUrl as string),
              ),
          );
          if (!already) {
            chat.messages = [
              ...chat.messages,
              { id: crypto.randomUUID(), role: "assistant", parts: [{ type: "text", text: assistantContent }] } as UIMessage,
            ];
          }
        } catch {
          /* reseed pós-conclusão cobre */
        }
        finishActiveAware(false);
        void maybePollTitle(convId);
      } catch (err) {
        if (abort.signal.aborted) {
          // Cancelado pelo usuário: limpa estado sem marcar falha.
          removeGenerating(convId);
          imageGeneratingRef.current.delete(convId);
          setImageStatusFor(convId, null);
          return;
        }
        const recovered = await recoverImageFromDb(convId);
        if (recovered) {
          try {
            chat.messages = [
              ...chat.messages,
              { id: crypto.randomUUID(), role: "assistant", parts: [{ type: "text", text: `![Imagem gerada](${recovered})` }] } as UIMessage,
            ];
          } catch { /* reseed cobre */ }
          finishActiveAware(false);
          return;
        }
        if (convId === activeConvRef.current)
          toast.error(`Erro na geração de imagem: ${(err as Error)?.message ?? ""}`);
        finishActiveAware(true);
      }
    },
    [
      ensureTextChat,
      clearUnread,
      addGenerating,
      removeGenerating,
      setImageStatusFor,
      setUnreadFor,
      notifyDone,
      evictIfIdle,
      recoverImageFromDb,
      maybePollTitle,
      queryClient,
    ],
  );

  const stop = useCallback(async (convId: string) => {
    const session = sessionsRef.current.get(convId);
    if (session) {
      if (session.kind === "image" && session.imageAbort) {
        try { session.imageAbort.abort(); } catch { /* ignore */ }
      } else {
        try { session.chat.stop(); } catch { /* ignore */ }
      }
    }
    removeGenerating(convId);
    setImageStatusFor(convId, null);
    imageGeneratingRef.current.delete(convId);
    try {
      await supabase
        .from("conversations")
        .update({ generation_status: "cancelled" } as never)
        .eq("id", convId);
    } catch {
      /* ignore */
    }
  }, [removeGenerating, setImageStatusFor]);

  const releaseView = useCallback(
    (convId: string) => {
      // Chamado pelo ChatPage no unmount. Evicta se idle e não-ativa.
      evictIfIdle(convId);
    },
    [evictIfIdle],
  );

  const isGenerating = useCallback((convId: string) => generating.has(convId), [generating]);
  const imageStatusFor = useCallback((convId: string) => imageStatus.get(convId), [imageStatus]);
  const unreadFor = useCallback((convId: string) => unread.get(convId), [unread]);

  const value = useMemo<GenerationContextValue>(
    () => ({
      ensureTextChat,
      getChat,
      startText,
      startImage,
      stop,
      releaseView,
      generating,
      unread,
      imageStatus,
      isGenerating,
      imageStatusFor,
      unreadFor,
      clearUnread,
    }),
    [
      ensureTextChat,
      getChat,
      startText,
      startImage,
      stop,
      releaseView,
      generating,
      unread,
      imageStatus,
      isGenerating,
      imageStatusFor,
      unreadFor,
      clearUnread,
    ],
  );

  return <GenerationContext.Provider value={value}>{children}</GenerationContext.Provider>;
};

export const useGeneration = (): GenerationContextValue => {
  const ctx = useContext(GenerationContext);
  if (!ctx) throw new Error("useGeneration must be used within GenerationProvider");
  return ctx;
};
